import { XIcon } from '@phosphor-icons/react';
import { useRef, type InputHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './Button.js';
import { cx } from './cx.js';

/**
 * The field look, worn by every text input and by `Select`.
 *
 * Five elements carried these sixteen utilities verbatim — four inputs and the
 * select — which is four chances for one of them to drift and nobody to notice,
 * because a field that is a hair off only looks wrong next to the others.
 *
 * A field is a chip that has been squared off: the same white face and 2px ink
 * stroke every control in this brand wears, on a soft rectangle rather than a
 * pill, because a pill full of text reads as a button a player should press.
 * It carries no offset shadow for the same reason: a field is a hole you type
 * into, not a chip sitting on the page.
 *
 * FOCUS: a field never grows a second ring. Focus doubles the stroke.
 *
 * The base `:focus-visible` rule draws 3px at 2px offset, and on every other
 * control that is right, because the ring is the only ink near it — or, on a
 * button, the offset shadow sits it on top of the chip rather than around it.
 * On a field it is wrong at any color: the field already wears a 2px ink
 * stroke, so an offset ring is a third concentric line — border, sliver of
 * ground, ring — and the focused field reads as a target drawn around the
 * field instead of the field being typed in. Two versions shipped and both
 * failed the eye: full ink was a bullseye, ink/55 was a dimmer bullseye. The
 * shape was the problem, not the volume.
 *
 * So the outline hugs the border instead: 3px at -1px offset, same ink, which
 * is 1px of overlap and 2px of growth. The overlap is not decoration — at
 * offset 0 the outline's inner edge and the border's outer edge are two
 * antialiased curves meeting at one coordinate, and a hairline of ground
 * showed through the join. Tucked 1px under, the seam is gone at every zoom.
 * Focused, the stroke reads as one continuous 4px line — the pen pressing
 * harder. That
 * is the same sentence the brand's selected states speak (`Switch` checked is
 * an ink track; a selected chip is ink): state lands on the control's own
 * anatomy, it does not decorate around it. `aria-invalid` keeps its promise
 * under focus — the outline follows the border to `red`, so an invalid
 * focused field is one 4px red stroke, not a mud of red and black.
 *
 * The accessibility arithmetic, measured rather than assumed. WCAG wants the
 * focus indicator to be at least a 2px perimeter with 3:1 against what it
 * changes over. The indicator here is the added 2px of stroke, drawn over the
 * ground: ink is 15.8:1 on `--ground` and 17.05:1 on white; red is 4.82:1
 * and 5.2:1. All four clear the bar with room, where the offset-ring versions
 * were shaving it (ink/55 sat at 3.76:1).
 *
 * The focus pair is the part that must not be retyped, and it is the part that
 * looks most like it could be deleted. `<input type="text">` and `<select>`
 * match `:focus-visible` after a MOUSE click, not only after a Tab — only
 * `<button>` tells the two apart. `focus:outline-none` takes the base layer's
 * offset ring away; `focus-visible:outline-*` draws the snug one. Neither half
 * survives alone: a review once dropped the restatement as redundant, and
 * measured after, a Tab-focused select reported `outline-style: none` —
 * `outline-none` outranks the base layer and strips the keyboard's ring too.
 *
 * That is exactly the kind of default a caller forgets, which is what puts this
 * on the third rung rather than in a comment telling people to remember it.
 *
 * Two different failure modes were found in the four copies, from two ends. The
 * focus pair is what a copy gets WRONG, because you cannot see what
 * `focus:outline-none` is for and it reads as deletable. The ARIA relationship
 * is what a copy OMITS: of the four, only `AuthScreen` and `PlayScreen` wired
 * `aria-invalid` and `aria-describedby`, so two fields rendered a hint or an
 * error beside them with nothing pointing at it.
 *
 * This component fixes the first and cannot fix the second — it does not know
 * what the hint says. `aria-describedby` is still the caller's to pass, or
 * `TextField`'s, which wires it for the line under the field. `ProfileScreen`
 * passes it now, and `SettingsList` no longer draws a field: its name moved to
 * Settings → Account, which draws `TextField`.
 *
 * What the component can do with `aria-invalid` is draw it, and it now does:
 * the stroke goes to `red` when the caller sets the attribute, so a field a
 * screen reader calls invalid also looks it. That used to be nothing at all.
 *
 * `Select` is the only other wearer. A third updates `brain/brand/BRAND.md`
 * first: the field look is a row in the brand's primitive table, not a string
 * to reuse.
 */
export const fieldLook =
  'min-h-12 w-full rounded-2xl border-2 border-ink bg-ground-light px-4 py-2 text-ink transition-control duration-150 ease-out aria-invalid:border-red focus:outline-none focus-visible:outline-3 focus-visible:outline-solid focus-visible:outline-ink focus-visible:-outline-offset-1 aria-invalid:focus-visible:outline-red';

/*
 * `/70`, not `/40`. This is a threshold, not a taste, and a light ground did
 * not make it go away. Measured against the field's own white face: full ink
 * contrasts 17.0:1, ink at 40% is 2.49:1 and ink at 55% is 3.84:1, both of
 * which fail WCAG AA for normal text at 4.5:1. 70% measures 6.29:1 and
 * passes. The dark field had the same shape of problem one step lower, and
 * the alpha sweep that moved it from 35 to 40 made it better without making it
 * pass, because a rule enforced by eye cannot know which step clears a
 * threshold. Still visibly dimmer than the value, which is all the placeholder
 * has to say.
 */
const PLACEHOLDER = 'placeholder:text-gray-700';

interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'style'> {
  /**
   * Layout only. The field's own surface and focus behavior are not yours. A
   * search field puts this on the box that holds the field and its clear.
   */
  readonly className?: string | undefined;
}

interface PlainInputProps extends FieldProps {
  /**
   * `code` is the room-code field: display face, spread wide, and upper-cased,
   * so six characters read as six characters rather than as a word.
   */
  readonly variant?: 'text' | 'code' | undefined;
}

/**
 * The search register: the same field in the searchbox role, with a clear at
 * its right end while it holds text.
 *
 * Controlled only. The clear has to know when there is something to clear and
 * has to empty the value, so the caller owns both through `value` and
 * `onClear`. The role and the type are the component's, so a caller cannot
 * turn a search field back into a plain one by accident.
 */
interface SearchInputProps extends Omit<FieldProps, 'value' | 'type' | 'role'> {
  readonly variant: 'search';
  readonly value: string;
  /** Empty the field. Focus comes back to it afterward; that part is not yours. */
  readonly onClear: () => void;
}

export type TextInputProps = PlainInputProps | SearchInputProps;

/** What each register adds to the field look. */
const REGISTER = {
  text: undefined,
  code: 'font-display text-lg tracking-widest uppercase',
  /* `pr-12` keeps typed text clear of the clear at the right end. */
  search: 'pr-12',
} as const satisfies Record<NonNullable<TextInputProps['variant']>, string | undefined>;

export function TextInput(props: TextInputProps): React.JSX.Element {
  if (props.variant === 'search') return <SearchInput {...props} />;
  const { variant = 'text', className, ...rest } = props;
  return (
    /* `type` before the spread so a caller can still ask for password or email. */
    <input
      type="text"
      {...rest}
      className={cx(fieldLook, PLACEHOLDER, REGISTER[variant], className)}
    />
  );
}

/**
 * Why this is not `type="search"`: that type makes Chrome and Safari draw
 * their own clear control inside the field, in the platform's blue, and
 * `accent-color` does not reach it. The rules sketch found it on 2026-09-23.
 * A text input in the `searchbox` role is the same thing to a screen reader
 * and to a role-based locator, and it leaves the clear for the brand to draw.
 *
 * The clear is an icon and nothing else (Mitch, 2026-09-23): a bare Phosphor
 * `X` on no face, with the ghost's `ink/5` hover wash and one-pixel press, so
 * it reacts like every other control without looking like a chip sitting in a
 * field. It is a ghost `Button` at `icon-sm`, which paints a 28px circle, set
 * 12px in from the field's edge, and extends it to a 48px target.
 *
 * The circle is sized by the focus ring, not by the icon. The base ring draws
 * 3px at a 2px offset, so it rings out 10px wider than the circle, inside a
 * field with 44px between its strokes. A 36px circle rang out past the stroke.
 * A 32px one rang out to 42px and was measured touching it, which read as the
 * X jammed against the field's edge. At 28px the ring is 38px and has air.
 *
 * The clear is not drawn while the field is empty or disabled. Pressing it
 * hands focus back to the field, because the button it was on disappears and
 * the player who cleared a search is about to type another. That is the
 * default a caller would forget, and the reason this is a variant rather than
 * a button laid over a field at each call site.
 */
function SearchInput(props: SearchInputProps): React.JSX.Element {
  const { variant, value, onClear, className, disabled, ...rest } = props;
  const { t } = useTranslation();
  const field = useRef<HTMLInputElement>(null);
  return (
    <div className={cx('relative', className)}>
      <input
        enterKeyHint="search"
        {...rest}
        ref={field}
        type="text"
        role="searchbox"
        value={value}
        disabled={disabled}
        className={cx(fieldLook, PLACEHOLDER, REGISTER[variant])}
      />
      {value !== '' && disabled !== true && (
        <span className="absolute inset-y-0 right-3 flex items-center">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t('common.clearSearch')}
            onClick={() => {
              onClear();
              field.current?.focus();
            }}
          >
            <XIcon size={18} weight="bold" aria-hidden="true" focusable="false" />
          </Button>
        </span>
      )}
    </div>
  );
}
